/** Atomic work-order planning, planning transition and scheduling forms. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { PersonReference } from "@/features/entities/PersonReference";
import { SearchInput } from "@/shared/components/filter-bar";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field } from "@/shared/ui/form";
import { Dialog } from "@/shared/ui/primitives";
import {
  type WorkOrderDetail,
  type WorkOrderPlanningCommand,
  type WorkOrderPlanningFields,
  useApplyPlanningCommand,
  useWorkOrderAssigneeOptions,
} from "./queries";
import { WorkOrderPlanningFields as PlanningFields } from "./WorkOrderPlanningFields";

export type PlanningAction = "edit" | "plan" | "schedule";

/** Show the normal planning form for an edit, Plan action or Schedule action. */
export function WorkOrderPlanning({
  wo,
  action,
  onClose,
}: {
  wo: WorkOrderDetail;
  action: PlanningAction;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const save = useApplyPlanningCommand(wo.id);
  const [values, setValues] = useState<WorkOrderPlanningFields>(() => ({
    assigned_team_id: wo.assigned_team?.id ?? null,
    vendor_id: wo.vendor?.id ?? null,
    operating_unit_id: wo.operating_unit?.id ?? null,
    cost_center_id: wo.cost_center?.id ?? null,
    due_at: wo.due_at,
    scheduled_start: wo.scheduled_start,
    scheduled_end: wo.scheduled_end,
    est_hours: wo.est_hours,
    restriction_scope: wo.restriction_scope,
  }));
  const [assigneeIds, setAssigneeIds] = useState<string[]>(() =>
    wo.assignees.map((assignee) => assignee.membership_id),
  );
  const canChooseAssignees = can("workorders.workorder.plan") && can("identity.user.view");
  const [search, setSearch] = useState<string>();
  const assigneeOptions = useWorkOrderAssigneeOptions(
    action === "schedule" && canChooseAssignees,
    search,
  );
  const scheduledStart =
    typeof values.scheduled_start === "string" ? values.scheduled_start : undefined;
  const scheduledEnd = typeof values.scheduled_end === "string" ? values.scheduled_end : undefined;
  const invalidSchedule =
    action === "schedule" &&
    (!scheduledStart ||
      !scheduledEnd ||
      Number.isNaN(new Date(scheduledStart).getTime()) ||
      Number.isNaN(new Date(scheduledEnd).getTime()) ||
      new Date(scheduledEnd).getTime() <= new Date(scheduledStart).getTime());
  const command = (nextAction: "save" | "plan" | "schedule"): void => {
    const payload: WorkOrderPlanningCommand = {
      action: nextAction,
      ...values,
      ...(nextAction === "schedule" && canChooseAssignees ? { assignee_ids: assigneeIds } : {}),
    };
    save.mutate(payload, { onSuccess: onClose });
  };
  const updateField = <K extends keyof WorkOrderPlanningFields>(
    field: K,
    value: WorkOrderPlanningFields[K],
  ): void => setValues((current) => ({ ...current, [field]: value }));
  const title =
    action === "schedule"
      ? t("workOrders.transition.schedule")
      : action === "plan"
        ? t("workOrders.planning")
        : t("workOrders.editPlanning");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title={title} wide>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          command(action === "schedule" ? "schedule" : action === "plan" ? "plan" : "save");
        }}
      >
        <PlanningFields
          values={values}
          onChange={updateField}
          prefix="wo-planning"
          scopeLocked={Boolean(wo.actual_start)}
        />
        {action === "schedule" ? (
          <>
            <p className="text-sm text-muted-foreground">
              {t("workOrders.deadlineVsBookedWindow")}
            </p>
            {canChooseAssignees ? (
              <Field id="planning-assignees" label={t("workOrders.assignees")}>
                <div className="space-y-2">
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder={t("workOrders.searchAssignees")}
                  />
                  <div
                    className="max-h-52 overflow-y-auto rounded-md border p-2"
                    role="group"
                    aria-label={t("workOrders.assignees")}
                  >
                    {assigneeOptions.query.isPending ? (
                      <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
                    ) : assigneeOptions.query.isError ? (
                      <p role="alert" className="text-sm text-destructive">
                        {errorMessage(assigneeOptions.query.error)}
                      </p>
                    ) : assigneeOptions.items.length ? (
                      assigneeOptions.items.map((member) => (
                        <Checkbox
                          key={member.id}
                          label={member.user.display_name || member.user.email}
                          checked={assigneeIds.includes(member.id)}
                          disabled={save.isPending}
                          onChange={(event) =>
                            setAssigneeIds((current) =>
                              event.target.checked
                                ? [...new Set([...current, member.id])]
                                : current.filter((id) => id !== member.id),
                            )
                          }
                        />
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t("workOrders.noAssigneeOptions")}
                      </p>
                    )}
                    {assigneeOptions.hasMore ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={assigneeOptions.isLoadingMore}
                        onClick={assigneeOptions.loadMore}
                        className="mt-1 w-full"
                      >
                        {t("workOrders.loadMoreAssignees")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Field>
            ) : (
              <p className="text-sm">
                {t("workOrders.assignees")}:{" "}
                {wo.assignees.length
                  ? wo.assignees.map((assignee, index) => (
                      <span key={assignee.membership_id}>
                        {index ? ", " : null}
                        <PersonReference
                          id={assignee.membership_id}
                          name={assignee.display_name}
                          context={{
                            kind: "work-order",
                            workOrderId: wo.id,
                            assignmentRole: assignee.role,
                          }}
                        />
                      </span>
                    ))
                  : t("workOrders.unassigned")}
              </p>
            )}
          </>
        ) : null}
        {save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(save.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
            {t("app.cancel")}
          </Button>
          {action === "plan" ? (
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={() => command("save")}
            >
              {t("workOrders.saveDraft")}
            </Button>
          ) : null}
          <Button type="submit" disabled={save.isPending || invalidSchedule}>
            {action === "schedule"
              ? t("workOrders.transition.schedule")
              : action === "plan"
                ? t("workOrders.saveAndPlan")
                : t("app.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
